import React from 'react';
import { DARK_THEME } from '../../lib/theme.js';

export function DataDeletionPage() {
  const theme = DARK_THEME;

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.bg,
      color: theme.text,
      fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
    }}>
      <SimpleHeader theme={theme} />

      <article style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: '40px 20px 80px',
        lineHeight: 1.65,
        fontSize: 15,
      }}>
        <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: -1, margin: 0 }}>Sletning af brugerdata</h1>
        <p style={{ color: theme.textMuted, fontSize: 13, margin: '8px 0 32px' }}>
          Sidst opdateret: 8. maj 2026
        </p>

        <Section title="Sådan får du dine data slettet">
          <p>
            Du har til enhver tid ret til at få dine personlige data slettet fra Tag Team.
            Det er en grundlæggende rettighed under GDPR (Databeskyttelsesforordningen),
            og vi gør det så nemt som muligt.
          </p>
        </Section>

        <Section title="Mulighed 1: Slet din konto i appen">
          <p>Den hurtigste vej er via appen selv:</p>
          <ol>
            <li>Log ind på <a href="https://www.gettagteam.com" style={linkStyle(theme)}>Tag Team</a></li>
            <li>Gå til <strong>Indstillinger</strong></li>
            <li>Klik på <strong>"Slet min konto"</strong> nederst</li>
            <li>Bekræft at du vil slette kontoen</li>
          </ol>
          <p>Når du sletter din konto, fjerner vi:</p>
          <ul>
            <li>Din profil (navn, billede, email)</li>
            <li>Alle opgaver og indkøbsvarer du har oprettet</li>
            <li>Statistikker, achievements og kærlighedspoint</li>
            <li>Beskeder og notifikationer</li>
            <li>Eventuel parring med din partner</li>
          </ul>
          <p>
            Sletningen sker permanent inden for <strong>30 dage</strong>. Visse data kan
            beholdes længere hvis loven kræver det (f.eks. faktureringsoplysninger ved
            tidligere køb opbevares i 5 år iht. dansk regnskabslov).
          </p>
        </Section>

        <Section title="Mulighed 2: Anmod om sletning via email">
          <p>
            Hvis du ikke længere har adgang til appen, eller du ønsker en formel anmodning,
            kan du sende en email til:
          </p>
          <p style={{ margin: '12px 0', fontSize: 17 }}>
            📧 <strong>hej@gettagteam.com</strong>
          </p>
          <p>I emailen, angiv:</p>
          <ul>
            <li>Den email-adresse din konto er knyttet til</li>
            <li>Hvor du har logget ind fra (Google, Facebook eller email)</li>
            <li>At du ønsker permanent sletning af din konto</li>
          </ul>
          <p>
            Vi bekræfter modtagelse inden for 48 timer og gennemfører sletningen
            inden for <strong>30 dage</strong> i overensstemmelse med GDPR.
          </p>
        </Section>

        <Section title="Hvis du logget ind via Facebook">
          <p>
            Hvis du har tilmeldt dig Tag Team via Facebook Login, og du sletter Tag Team-kontoen
            i din Facebook-konto:
          </p>
          <ol>
            <li>Gå til <strong>Facebook</strong> → <strong>Indstillinger</strong> → <strong>Apps og hjemmesider</strong></li>
            <li>Find <strong>Tag Team</strong> i listen</li>
            <li>Klik <strong>Fjern</strong></li>
          </ol>
          <p>
            Når du gør dette, modtager Tag Team en automatisk besked fra Facebook om at
            slette dine data. Vi behandler anmodningen og fjerner dine data inden for 30 dage.
          </p>
        </Section>

        <Section title="Hvis du logget ind via Google">
          <p>Du kan tilbagekalde Tag Team's adgang til din Google-konto:</p>
          <ol>
            <li>Gå til <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" style={linkStyle(theme)}>myaccount.google.com/permissions</a></li>
            <li>Find <strong>Tag Team</strong> i listen</li>
            <li>Klik <strong>Fjern adgang</strong></li>
          </ol>
          <p>
            Bemærk: Det her stopper Google fra at dele data med os fremadrettet, men sletter
            ikke automatisk dine data hos os. Brug en af de to ovenstående metoder for det.
          </p>
        </Section>

        <Section title="Hvad sker der efter sletning?">
          <ul>
            <li>Din konto er <strong>øjeblikkeligt deaktiveret</strong> og kan ikke logges ind</li>
            <li>Dine data <strong>permanent slettes inden for 30 dage</strong></li>
            <li>Din partner får ikke automatisk besked, men de vil opdage at parringen er ophævet næste gang de åbner appen</li>
            <li>Anonymiserede aggregerede statistikker (uden personlige data) kan beholdes til app-forbedring</li>
          </ul>
        </Section>

        <Section title="Spørgsmål?">
          <p>
            Har du spørgsmål til sletning af dine data eller dine GDPR-rettigheder?
          </p>
          <p>
            📧 <strong>hej@gettagteam.com</strong><br />
            📍 <strong>Anka Media, Varde, Danmark</strong>
          </p>
          <p>
            Du kan også klage til <strong>Datatilsynet</strong> hvis du mener vi ikke
            håndterer dine data korrekt: <a href="https://www.datatilsynet.dk" target="_blank" rel="noopener noreferrer" style={linkStyle(theme)}>www.datatilsynet.dk</a>
          </p>
        </Section>
      </article>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 21, fontWeight: 700, letterSpacing: -0.4, margin: '0 0 12px' }}>{title}</h2>
      {children}
    </section>
  );
}

function linkStyle(theme) {
  return {
    color: theme.coral,
    textDecoration: 'underline',
  };
}

function SimpleHeader({ theme }) {
  return (
    <header style={{
      padding: '14px 20px',
      borderBottom: `1px solid ${theme.border}`,
    }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <a href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: theme.text }}>
          <svg width="28" height="28" viewBox="0 0 120 120">
            <g transform="translate(60,60)">
              <rect x="-30" y="-20" width="20" height="40" rx="3" fill={theme.coral}/>
              <rect x="-36" y="-20" width="32" height="9" rx="3" fill={theme.coral}/>
              <rect x="6" y="-22" width="20" height="40" rx="3" fill={theme.gold}/>
              <rect x="0" y="-22" width="32" height="9" rx="3" fill={theme.gold}/>
            </g>
          </svg>
          <span style={{ fontSize: 16, fontWeight: 700 }}>Tag Team</span>
        </a>
      </div>
    </header>
  );
}
